<template>
  <div
      v-if="isVisible"
      class="modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-queue-title"
  >
    <!-- Backdrop -->
    <Transition name="fade">
      <div v-if="isVisible" class="modal-backdrop" @click="close" />
    </Transition>

    <!-- Panel wrapper -->
    <Transition name="slide-up">
      <div v-if="isVisible" class="modal-overlay">
        <div class="modal-panel" ref="modalPanel" tabindex="-1" @click.stop>

          <!-- ========== HEADER ========== -->
          <header class="modalheader2">
            <div class="header-top">
              <div class="header-brand">
                <div class="header-icon">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div>
                  <h2 id="order-queue-title" class="header-title">
                    Commandes en file d'attente
                  </h2>
                  <p class="header-subtitle">
                    {{ filteredData.length }} commande{{ filteredData.length > 1 ? 's' : '' }} en attente
                  </p>
                </div>
              </div>
              <button @click="close" class="header-close-btn" aria-label="Fermer">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Search + Controls -->
            <div class="header-controls">
              <div class="search-box">
                <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                    v-model="searchQuery"
                    ref="searchInput"
                    type="search"
                    class="search-input"
                    placeholder="Client, n° document, table..."
                    @keyup.enter="selectFirstResult"
                />
                <button
                    v-if="searchQuery"
                    @click="clearSearch"
                    class="search-clear-btn"
                    aria-label="Effacer"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div class="flex items-center gap-2">
                <label class="per-page-label">Lignes</label>
                <select v-model="perPage" class="per-page-select">
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                </select>
              </div>
            </div>
          </header>

          <!-- Results bar -->
          <div class="results-bar">
                        <span class="results-count">
                            <strong>{{ filteredData.length }}</strong>
                            résultat{{ filteredData.length > 1 ? 's' : '' }}
                            <em v-if="searchQuery"> pour "{{ searchQuery }}"</em>
                        </span>
            <button
                @click="refreshData"
                :disabled="isLoading"
                class="refresh-btn"
                title="Actualiser"
            >
              <svg :class="['w-4 h-4', { 'animate-spin': isLoading }]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Actualiser
            </button>
          </div>

          <!-- ========== CONTENT ========== -->
          <div class="modal-body">

            <!-- Loading -->
            <div v-if="isLoading" class="state-container">
              <svg class="w-10 h-10 animate-spin state-spinner" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <p class="state-text">Chargement des commandes...</p>
            </div>

            <!-- Error -->
            <div v-else-if="errorMessage" class="state-container">
              <div class="state-icon state-icon--error">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
              </div>
              <p class="state-text">{{ errorMessage }}</p>
              <button @click="refreshData" class="retry-btn">Réessayer</button>
            </div>

            <!-- Data -->
            <template v-else>
              <!-- Desktop Table -->
              <div class="table-wrapper hidden md:block">
                <table class="data-table">
                  <thead>
                  <tr>
                    <th style="width:44px">N°</th>
                    <th>N° Document</th>
                    <th>Client</th>
                    <th>Table</th>
                    <th>Serveur</th>
                    <th class="text-right">Montant</th>
                    <th>Créé par</th>
                    <th>Date</th>
                    <th class="text-center" style="width:130px">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <template v-if="paginatedData.length">
                    <tr
                        v-for="(item, index) in paginatedData"
                        :key="item.numdoc || index"
                        class="table-row"
                        @dblclick="selectOrder(item)"
                    >
                      <td>
                                                    <span class="row-num">
                                                        {{ (currentPage - 1) * perPage + index + 1 }}
                                                    </span>
                      </td>
                      <td>
                        <span class="doc-badge">{{ item.numdoc }}</span>
                      </td>
                      <td>
                        <div class="client-cell">
                          <div class="client-avatar">{{ getInitials(item.client) }}</div>
                          <span class="client-name">{{ item.client || '-' }}</span>
                        </div>
                      </td>
                      <td>
                                                    <span v-if="item.commandetable" class="table-badge">
                                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                                  d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                                                        </svg>
                                                        {{ item.commandetable }}
                                                    </span>
                        <span v-else class="cell-empty">-</span>
                      </td>
                      <td class="cell-secondary">{{ item.serveur || '-' }}</td>
                      <td class="text-right">
                        <span class="amount-value">{{ formatCurrency(item.mtntcc) }}</span>
                      </td>
                      <td class="cell-secondary">{{ item.saisiepar || '-' }}</td>
                      <td class="cell-secondary">{{ item.creerler || '-' }}</td>
                      <td>
                        <div class="action-btns">
                          <button
                              @click.stop="selectOrder(item)"
                              class="btn-load"
                              title="Charger cette commande"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                            </svg>
                            Charger
                          </button>
                          <button
                              v-if="parseFloat(item.nuse) !== 1"
                              @click.stop="confirmDelete(item)"
                              class="btn-delete"
                              title="Supprimer"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-else>
                    <td colspan="9">
                      <div class="empty-state">
                        <div class="empty-icon">
                          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                          </svg>
                        </div>
                        <p class="empty-title">Aucune commande en attente</p>
                        <p class="empty-subtitle">
                          {{ searchQuery ? 'Modifiez votre recherche' : "La file d'attente est vide" }}
                        </p>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile Cards -->
              <div class="md:hidden p-3 space-y-3">
                <template v-if="paginatedData.length">
                  <div
                      v-for="(item, index) in paginatedData"
                      :key="item.numdoc || index"
                      class="mobile-card"
                      @click="selectOrder(item)"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <div class="flex items-center gap-2">
                                                <span class="mobile-num">
                                                    {{ (currentPage - 1) * perPage + index + 1 }}
                                                </span>
                        <div>
                          <p class="doc-badge" style="display:inline-flex">{{ item.numdoc }}</p>
                          <p class="cell-secondary text-xs mt-0.5">{{ item.creerler }}</p>
                        </div>
                      </div>
                      <span class="amount-value">{{ formatCurrency(item.mtntcc) }}</span>
                    </div>

                    <div class="grid grid-cols-2 gap-y-2 text-sm">
                      <div>
                        <p class="mobile-label">Client</p>
                        <p class="mobile-value">{{ item.client || '-' }}</p>
                      </div>
                      <div>
                        <p class="mobile-label">Table</p>
                        <p class="mobile-value">{{ item.commandetable || '-' }}</p>
                      </div>
                      <div>
                        <p class="mobile-label">Serveur</p>
                        <p class="mobile-value">{{ item.serveur || '-' }}</p>
                      </div>
                      <div>
                        <p class="mobile-label">Créé par</p>
                        <p class="mobile-value">{{ item.saisiepar || '-' }}</p>
                      </div>
                    </div>

                    <div class="mobile-card-footer">
                                            <span class="mobile-load-label">
                                                Charger
                                                <svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                                </svg>
                                            </span>
                    </div>
                  </div>
                </template>

                <div v-else class="empty-state" style="padding: 3rem 1rem">
                  <div class="empty-icon">
                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <p class="empty-title">Aucune commande</p>
                  <p class="empty-subtitle">
                    {{ searchQuery ? 'Modifiez votre recherche' : "La file d'attente est vide" }}
                  </p>
                </div>
              </div>
            </template>
          </div>

          <!-- ========== FOOTER ========== -->
          <footer class="modal-footer">
            <p class="pagination-info">
              Page <strong>{{ currentPage }}</strong> /
              <strong>{{ totalPages || 1 }}</strong>
              <span class="hidden sm:inline">
                                &nbsp;({{ filteredData.length }} élément{{ filteredData.length > 1 ? 's' : '' }})
                            </span>
            </p>

            <div class="flex items-center gap-3">
              <nav class="pagination-nav" aria-label="Pagination">
                <button
                    @click="currentPage--"
                    :disabled="currentPage === 1"
                    class="page-btn"
                    aria-label="Page précédente"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>

                <template v-for="pageNum in displayedPages" :key="pageNum">
                  <span v-if="pageNum === '...'" class="page-ellipsis">···</span>
                  <button
                      v-else
                      @click="currentPage = pageNum"
                      :class="['page-btn', { active: currentPage === pageNum }]"
                  >{{ pageNum }}</button>
                </template>

                <button
                    @click="currentPage++"
                    :disabled="currentPage >= totalPages"
                    class="page-btn"
                    aria-label="Page suivante"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </nav>

              <button @click="close" class="btn-close-footer">Fermer</button>
            </div>
          </footer>
        </div>
      </div>
    </Transition>

    <ModalFormSuppression ref="modalFormSuppression" @closed="refreshData" />
  </div>
</template>

<script>
import axios from 'axios';
import { currencyFormatDE, randomString } from '@/allfunction';
import ModalFormSuppression from '@/components/views/pages/gestioncommerciale/achatvente/default/formsuppression';
import * as JwtService from '@/common/jwt.service';

export default {
  /* eslint-disable */
  name: 'OrderQueueModal',

  components: { ModalFormSuppression },

  emits: ['selectedfacture'],

  data() {
    return {
      isVisible: false,
      isLoading: false,
      errorMessage: '',
      dataTable: [],
      searchQuery: '',
      currentPage: 1,
      perPage: 10,
      searchDebounceTimer: null,
    };
  },

  computed: {
    filteredData() {
      if (!this.searchQuery.trim()) return this.dataTable;
      const q = this.searchQuery.toLowerCase().trim();
      return this.dataTable.filter(item =>
          (item.client?.toLowerCase() || '').includes(q) ||
          (item.numdoc?.toLowerCase() || '').includes(q) ||
          (item.commandetable?.toLowerCase() || '').includes(q) ||
          (item.serveur?.toLowerCase() || '').includes(q)
      );
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredData.slice(start, start + this.perPage);
    },

    totalPages() {
      return Math.ceil(this.filteredData.length / this.perPage) || 1;
    },

    displayedPages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (current >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', current - 1, current, current + 1, '...', total);
      }

      return pages;
    },
  },

  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => { this.currentPage = 1; }, 150);
    },
    perPage() {
      this.currentPage = 1;
    },
  },

  methods: {
    // ============ MODAL ============
    show(dataTable = []) {
      this.dataTable = dataTable;
      this.searchQuery = '';
      this.currentPage = 1;
      this.errorMessage = '';
      this.isVisible = true;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => this.$refs.modalPanel?.focus());
    },

    close() {
      this.isVisible = false;
      document.body.style.overflow = '';
    },

    // ============ DATA ============
    async refreshData() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const caisse = { cf: this.$store.state.caisseinfo, document: 'FV' };
        const url = `${process.env.VUE_APP_API_URL}/egl74587kjkjrfdkcaisse771714dfe/comptoire/caisse/${this.$store.state.caisseencour}/${this.$store.state.appsociete}/load895418init51comptoirerefdepdpcc11515/option2/${randomString(280)}`;
        const response = await axios.post(url, caisse, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JwtService.getToken()}`,
            'csrftoken': this.$store.state.appsociete,
          },
          withCredentials: true,
        });
        this.dataTable = response.data.success ? (response.data.m || []) : [];
        if (!response.data.success) {
          this.errorMessage = response.data.message || 'Erreur lors du chargement';
        }
      } catch (error) {
        this.errorMessage = 'Impossible de charger les commandes. Vérifiez votre connexion.';
        this.dataTable = [];
      } finally {
        this.isLoading = false;
      }
    },

    // ============ ACTIONS ============
    selectOrder(item) {
      this.$emit('selectedfacture', { item });
      this.close();
    },

    selectFirstResult() {
      if (this.paginatedData.length > 0) this.selectOrder(this.paginatedData[0]);
    },

    clearSearch() {
      this.searchQuery = '';
    },

    confirmDelete(item) {
      if (parseFloat(item.nuse) === 1) {
        toastr['error']('Impossible de supprimer cette commande.');
        return;
      }
      const title = `Voulez-vous supprimer le document ${item.numdoc} ?`;
      this.$refs.modalFormSuppression.show(title, item, 'F', 'vente');
    },

    // ============ UTILS ============
    formatCurrency(value) {
      return currencyFormatDE(value);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
    },

    // ============ KEYBOARD ============
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isVisible) this.close();
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
    clearTimeout(this.searchDebounceTimer);
  },
};
</script>

<style scoped>
/*
 * Ce composant hérite des variables CSS de .caisse-app (parent)
 * via la cascade CSS naturelle. Les fallbacks garantissent
 * le fonctionnement hors contexte.
 *
 * Variables héritées : --bg-card, --bg-input, --bg-hover,
 * --text-primary, --text-secondary, --text-muted,
 * --border, --primary, --warning, --success, --danger,
 * --radius-sm, --radius-md, --radius-lg, --transition
 */

/* Accent local : amber (file d'attente = warning) */
.modal-root {
  --accent: var(--warning, #f59e0b);
  --accent-dark: #d97706;
  --accent-light: rgba(245, 158, 11, 0.12);
}

/* ===== STRUCTURE ===== */
.modal-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  overflow: hidden;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
}

/* Centering wrapper - plein écran pour aligner le panel */
.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow-y: auto;
}

@media (min-width: 640px) {
  .modal-overlay {
    align-items: center;
    padding: 1rem;
  }
}

/* Panel principal */
.modal-panel {
  position: relative;
  width: 100%;
  max-width: 960px;
  background: var(--bg-card, #ffffff);
  border-radius: 1rem 1rem 0 0;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
  outline: none;
}

@media (min-width: 640px) {
  .modal-panel {
    border-radius: var(--radius-xl, 1.25rem);
    max-height: 88vh;
  }
}

/* ===== HEADER ===== */
.modalheader2 {
  flex-shrink: 0;
  padding: 1rem 1.25rem;
  background: darkgray !important;
  color: white;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.875rem;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md, 0.625rem);
  flex-shrink: 0;
}

.header-title {
  font-size: 17px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.header-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin: 2px 0 0;
}

.header-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: var(--radius-md, 0.625rem);
  color: white;
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.header-close-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}

/* Controls (search + per-page) */
.header-controls {
  display: flex;
  gap: 12px;
  flex-direction: column;
}

@media (min-width: 640px) {
  .header-controls { flex-direction: row; }
}

.search-box {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.65);
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 38px 0 38px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius-md, 0.625rem);
  color: white;
  font-size: 14px;
  transition: var(--transition, all 0.2s ease);
}

.search-input::placeholder { color: rgba(255, 255, 255, 0.6); }

.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.22);
}

/* Supprimer l'icône X native du type search */
.search-input::-webkit-search-cancel-button { display: none; }

.search-clear-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 50%;
  transition: var(--transition, all 0.2s ease);
}

.search-clear-btn:hover { color: white; background: rgba(255,255,255,0.15); }

.per-page-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;
}

.per-page-select {
  height: 40px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius-md, 0.625rem);
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.per-page-select:focus { outline: none; border-color: rgba(255,255,255,0.5); }
.per-page-select option { background: #1e293b; color: white; }

/* ===== RESULTS BAR ===== */
.results-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 16px;
  background: var(--bg-hover, #f1f5f9);
  border-bottom: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  color: var(--text-secondary, #64748b);
}

.results-count strong { color: var(--text-primary, #1e293b); font-weight: 600; }
.results-count em { font-style: normal; color: var(--accent); }

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: none;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: var(--radius-sm, 0.375rem);
  font-size: 12px;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.refresh-btn:hover:not(:disabled) {
  background: var(--bg-card, #fff);
  color: var(--accent);
  border-color: var(--accent);
}

.refresh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ===== MODAL BODY ===== */
.modal-body {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

/* ===== STATE CONTAINERS ===== */
.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
  flex: 1;
}

.state-spinner { color: var(--accent, #f59e0b); }

.state-text {
  font-size: 15px;
  color: var(--text-secondary, #64748b);
}

.state-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.state-icon--error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--danger, #ef4444);
}

.retry-btn {
  padding: 8px 20px;
  background: var(--accent);
  border: none;
  border-radius: var(--radius-md, 0.625rem);
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.retry-btn:hover { background: var(--accent-dark); }

/* ===== TABLE ===== */
.table-wrapper { overflow-x: auto; }

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: darkgray;
}

.data-table thead th {
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: white;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid var(--border, #e2e8f0);
  transition: background 0.12s ease;
}

.data-table tbody tr:last-child { border-bottom: none; }

.table-row { cursor: pointer; }
.table-row:hover { background: var(--bg-hover, #f1f5f9); }
.table-row:hover .row-num {
  background: var(--accent-light);
  color: var(--accent);
}

.data-table tbody td {
  padding: 10px 14px;
  font-size: 13px;
  color: var(--text-primary, #1e293b);
}

/* Table cells */
.row-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--bg-hover, #f1f5f9);
  border-radius: 50%;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
  transition: var(--transition, all 0.2s ease);
}

.doc-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  background: var(--bg-input, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: var(--radius-sm, 0.375rem);
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
}

.client-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.client-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary, #3b82f6) 0%, #2563eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.client-name {
  font-weight: 500;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: rgba(139, 92, 246, 0.1);
  color: #7c3aed;
  border-radius: var(--radius-sm, 0.375rem);
  font-size: 12px;
  font-weight: 500;
}

.amount-value {
  font-weight: 700;
  color: var(--success, #10b981);
  font-size: 13px;
  white-space: nowrap;
}

.cell-secondary { color: var(--text-secondary, #64748b); font-size: 13px; }
.cell-empty { color: var(--text-muted, #94a3b8); }

/* Action buttons */
.action-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-load {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: darkgray;
  border: none;
  border-radius: var(--radius-sm, 0.375rem);
  font-size: 12px;
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
  white-space: nowrap;
}

.btn-load:hover {
  background: var(--accent);
  color: white;
}

.btn-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(239, 68, 68, 0.1);
  border: none;
  border-radius: var(--radius-sm, 0.375rem);
  color: var(--danger, #ef4444);
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.btn-delete:hover {
  background: var(--danger, #ef4444);
  color: white;
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 1rem;
}

.empty-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--bg-hover, #f1f5f9);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: var(--text-muted, #94a3b8);
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  margin: 0 0 6px;
}

.empty-subtitle {
  font-size: 13px;
  color: var(--text-muted, #94a3b8);
  margin: 0;
}

/* ===== MOBILE CARDS ===== */
.mobile-card {
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: var(--radius-lg, 0.875rem);
  padding: 1rem;
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.mobile-card:hover, .mobile-card:active {
  background: var(--bg-hover, #f1f5f9);
  border-color: var(--accent);
}

.mobile-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--accent);
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: white;
}

.mobile-label {
  font-size: 11px;
  color: var(--text-muted, #94a3b8);
  margin: 0 0 2px;
}

.mobile-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #1e293b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
}

.mobile-card-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border, #e2e8f0);
}

.mobile-load-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: var(--accent);
}

/* ===== FOOTER ===== */
.modal-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background: var(--bg-hover, #f1f5f9);
  border-top: 1px solid var(--border, #e2e8f0);
}

.pagination-info {
  font-size: 13px;
  color: var(--text-secondary, #64748b);
  margin: 0;
}

.pagination-info strong {
  color: var(--text-primary, #1e293b);
  font-weight: 600;
}

.pagination-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 34px;
  height: 34px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: darkgray;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: var(--radius-sm, 0.375rem);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.page-btn:hover:not(:disabled) {
  background: var(--bg-hover, #f1f5f9);
  color: var(--text-primary, #1e293b);
}

/* FIX : text-white au lieu de text-black sur fond amber */
.page-btn.active {
  background: var(--accent);
  border-color: var(--accent);
  color: white;
  font-weight: 700;
}

.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.page-ellipsis {
  padding: 0 6px;
  color: var(--text-muted, #94a3b8);
  font-size: 16px;
  line-height: 34px;
}

.btn-close-footer {
  padding: 8px 18px;
  background: var(--text-secondary, #64748b);
  border: none;
  border-radius: var(--radius-md, 0.625rem);
  font-size: 13px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: var(--transition, all 0.2s ease);
}

.btn-close-footer:hover { background: var(--text-primary, #1e293b); }

/* ===== ANIMATIONS ===== */
/* Backdrop : simple fade */
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Panel : slide-up sur mobile, scale sur desktop */
.slide-up-enter-active {
  animation: panelSlideUp 0.32s cubic-bezier(0.34, 1.2, 0.64, 1);
}
.slide-up-leave-active {
  animation: panelSlideDown 0.2s ease forwards;
}

@keyframes panelSlideUp {
  from { opacity: 0; transform: translateY(48px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes panelSlideDown {
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(24px); }
}

@media (min-width: 640px) {
  .slide-up-enter-active {
    animation: panelScaleIn 0.3s cubic-bezier(0.34, 1.2, 0.64, 1);
  }
  .slide-up-leave-active {
    animation: panelScaleOut 0.2s ease forwards;
  }

  @keyframes panelScaleIn {
    from { opacity: 0; transform: scale(0.94) translateY(12px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  @keyframes panelScaleOut {
    from { opacity: 1; transform: scale(1) translateY(0); }
    to   { opacity: 0; transform: scale(0.96) translateY(8px); }
  }
}

/* ===== SCROLLBAR ===== */
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb {
  background: var(--text-muted, rgba(148,163,184,0.5));
  border-radius: 50px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary, rgba(100,116,139,0.7));
}
</style>